import { useEffect, useState } from "react";
import {
  useChat,
  useLocalParticipant,
  useTrackTranscription,
  useVoiceAssistant,
} from "@livekit/components-react";
import {
  ChatMessage,
  LocalParticipant,
  Participant,
  Track,
  TranscriptionSegment,
} from "livekit-client";
import { getValue, setValue } from "@/utils/storage";

export type UnifiedMessage = {
  id: string;
  text?: string;
  message?: string;
  timestamp: number;
  isSelf: boolean;
  isUser: boolean;
  lastReceivedTime?: number;
  final?: boolean;
  isMarkdown?: boolean;
};

const getStoredMessages = (): UnifiedMessage[] => {
  try {
    // ? Let's retrieve the old transcriptions from local storage so it's not a breaking change.
    const messagesJSON = getValue("messages") || getValue("transcriptions");
    const messages = JSON.parse(messagesJSON || "[]") as UnifiedMessage[];

    return messages;
  } catch {
    console.error("Failed to retrieve stored messages:", getValue("messages"));
    return [];
  }
};

const setStoredMessages = (messages: UnifiedMessage[]) => {
  setValue("messages", JSON.stringify(messages));
};

const mergeAndDeduplicateMessages = (
  ...messagesArrays: UnifiedMessage[][]
): UnifiedMessage[] => {
  const unique = new Map<string, UnifiedMessage>();
  const merged = messagesArrays.flat();

  // We use a Map to ensure we get the final message from transcriptions
  merged.forEach((msg) => {
    const existingMessage = unique.get(msg.id);
    if (existingMessage) {
      // If the message already exists, we keep the final one...
      if (msg.final) {
        unique.set(msg.id, msg);
        // ...or the one with the latest timestamp
      } else if (existingMessage.lastReceivedTime && msg.lastReceivedTime) {
        if (existingMessage.lastReceivedTime < msg.lastReceivedTime) {
          unique.set(msg.id, msg);
        }
      }
    } else {
      // If the message does not exist, we add it
      unique.set(msg.id, msg);
    }
  });

  return Array.from(unique.values());
};

export const useMessages = () => {
  const { chatMessages, send } = useChat();
  const { audioTrack: agentAudioTrack } = useVoiceAssistant();
  const agentTranscriptions = useTrackTranscription(agentAudioTrack);
  const localParticipant = useLocalParticipant();
  const localTranscriptions = useTrackTranscription({
    publication: localParticipant.microphoneTrack,
    source: Track.Source.Microphone,
    participant: localParticipant.localParticipant,
  });
  const [messages, setMessages] = useState<UnifiedMessage[]>(
    getStoredMessages().slice(-30),
  );

  useEffect(
    () => {
      // Save timestamp of interaction
      const localMessagesUnified: UnifiedMessage[] = segmentsToUnifiedMessages(
        localTranscriptions.segments,
        localParticipant.localParticipant,
      );
      const agentMessagesUnified: UnifiedMessage[] = segmentsToUnifiedMessages(
        agentTranscriptions.segments,
        agentAudioTrack?.participant,
      );
      const chatMessagesUnified: UnifiedMessage[] =
        chatMessagesToUnifiedMessages(
          chatMessages,
          localParticipant.localParticipant,
        );
      const mergedMessages = mergeAndDeduplicateMessages(
        messages,
        localMessagesUnified,
        agentMessagesUnified,
        chatMessagesUnified,
      );
      const sortedMessages = mergedMessages.sort(
        (a, b) => a.timestamp - b.timestamp,
      );

      setMessages(sortedMessages);
      setStoredMessages(sortedMessages);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      chatMessages,
      agentAudioTrack,
      agentTranscriptions.segments,
      localTranscriptions.segments,
      localParticipant.localParticipant,
    ],
  );

  return { messages, sendChat: send };
};

//Helper function to map chat messages from livekit to unified messages
function chatMessagesToUnifiedMessages(
  chatMessages: ChatMessage[],
  participant: Participant | undefined,
): UnifiedMessage[] {
  return chatMessages.map((msg) => ({
    id: msg.id,
    text: msg.message,
    isSelf: participant instanceof LocalParticipant,
    isUser: participant instanceof LocalParticipant,
    timestamp: msg.timestamp,
    message: msg.message,
    isMarkdown: isMarkdown(msg.message),
  }));
}
// Helper function to map transcription segments to unified messages
function segmentsToUnifiedMessages(
  segments: TranscriptionSegment[],
  participant: Participant | undefined,
): UnifiedMessage[] {
  return segments.map((segment) => ({
    id: segment.id,
    text: segment.final ? segment.text : `${segment.text} ...`,
    isSelf: participant instanceof LocalParticipant,
    isUser: participant instanceof LocalParticipant,
    timestamp: segment.firstReceivedTime,
    lastReceivedTime: segment.lastReceivedTime,
    final: segment.final,
    message: segment.text,
    isMarkdown: isMarkdown(segment.text),
  }));
}

function isMarkdown(text: string | undefined): boolean {
  if (!text) return false;

  const markdownPatterns = [
    /^#{1,6} .+/m, // headers
    /\*\*[^*]+\*\*/g, // bold
    /\*[^*]+\*/g, // italic
    /`[^`]+`/g, // inline code
    /```[\s\S]*?```/g, // code blocks
    /^(\*|-|\+|\d+\.)\s.+/m, // lists, using *, -, +, or numbers
    /\[.*?\]\(.*?\)/g, // links
    /!\[.*?\]\(.*?\)/g, // images
    /^> .+/m, // blockquotes
  ];

  return markdownPatterns.some((regex) => regex.test(text));
}
